/**
 * Tick — fixed-timestep deterministic loop.
 *
 * Phaser's default loop has a variable dt, which makes lockstep multiplayer
 * impossible: two phones running at 60fps and 30fps will drift apart.
 * This driver accumulates real time and dispatches `step(dt)` calls at a
 * constant cadence, decoupling simulation from rendering.
 *
 * The loop is driven by Phaser's scene update via `advance(realDtMs)`.
 */
export class Tick {
  /**
   * @param {object} opts
   * @param {number} opts.tickRateHz - Simulation frequency (e.g. 20).
   * @param {(stepIndex: number, dtSec: number) => void} opts.onStep
   * @param {number} [opts.maxStepsPerFrame=12] - Spiral-of-death guard. If we
   *   ever fall this many steps behind we drop the excess accumulator.
   *   Low-end phones need enough headroom to catch up after a slow
   *   frame, but too high a cap lets a genuinely stuck loop snowball.
   */
  constructor(opts) {
    this.tickRateHz = opts.tickRateHz;
    this.dtSec = 1 / opts.tickRateHz;
    this.dtMs = 1000 / opts.tickRateHz;
    this.onStep = opts.onStep;
    this.maxStepsPerFrame = opts.maxStepsPerFrame || 12;

    this._accumMs = 0;
    this._stepIndex = 0;
    this._running = false;
  }

  start() {
    this._accumMs = 0;
    this._stepIndex = 0;
    this._running = true;
  }

  stop() {
    this._running = false;
  }

  /** Call from your scene's update(time, delta) every frame. */
  advance(realDeltaMs) {
    if (!this._running) return;
    this._accumMs += realDeltaMs;

    let stepsThisFrame = 0;
    while (this._accumMs >= this.dtMs) {
      this._accumMs -= this.dtMs;
      this.onStep(this._stepIndex, this.dtSec);
      this._stepIndex += 1;
      stepsThisFrame += 1;
      if (stepsThisFrame >= this.maxStepsPerFrame) {
        // Spiral of death — drop the rest of the backlog.
        this._accumMs = 0;
        break;
      }
    }
  }

  /** Interpolation alpha [0, 1) between the last step and the next. */
  getAlpha() {
    return this._accumMs / this.dtMs;
  }

  getStepIndex() {
    return this._stepIndex;
  }
}
